// May 28, 2026 — Surface-level grey-out wrapper.
//
// Wraps any LLM-triggering tile/button. When the backend Zero Balance
// guard says the surface is blocked (no BYOK + empty bucket), the
// wrapper:
//   • Renders children at 40% opacity with a lock badge overlay.
//   • Intercepts clicks and pops the existing LLMGuardModal teach-popup.
//
// Excludes Fan Portal use — never wrap Fan Portal tiles. Those calls
// debit the silo owner, not the visitor.
//
// Usage:
//   <LLMGuardWrap surface="workbench_run" siloId={silo?.id}>
//     <button onClick={runWorkbench}>Run</button>
//   </LLMGuardWrap>
import { Lock } from "@phosphor-icons/react";
import { useLLMGuardState, triggerLLMGuardModal } from "@/lib/useLLMGuardState";

export default function LLMGuardWrap({ surface, siloId = null, enabled = true, children, className = "", onBlockedClick = null }) {
  const { blocked, provider, consoleUrl, loading } = useLLMGuardState(surface, siloId, { enabled });

  if (loading || !blocked) {
    // Even when the guard isn't blocking, we must still apply the
    // positional className passed in by the caller — otherwise wrappers
    // that rely on absolute positioning (e.g., the public Q&A Send button
    // pinned to the textarea's top-right corner) get dropped out of flow.
    // Bug surfaced: PublicSilo composer Send button floating below the
    // input on desktop. Fix: wrap in a passthrough div when className set.
    return className ? <div className={className}>{children}</div> : <>{children}</>;
  }

  const onClickIntercept = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Some surfaces (e.g. the public-Q&A Send button, which only the OWNER
    // ever sees blocked) supply their own handler — the global teach-modal
    // deliberately ignores /s/ pages, so we defer to the caller there.
    if (onBlockedClick) { onBlockedClick({ provider, surface, consoleUrl }); return; }
    triggerLLMGuardModal({ provider, surface, consoleUrl });
  };

  return (
    <div
      data-testid={`llm-guard-wrap-${surface}`}
      data-blocked="true"
      className={className || "relative inline-block"}
      onClickCapture={onClickIntercept}
    >
      <div className="opacity-40 pointer-events-none select-none">
        {children}
      </div>
      <div
        className="absolute inset-0 flex items-center justify-center cursor-pointer"
        title={`${provider || "LLM"} access locked — tap to fix`}
      >
        <span className="bg-[#C16E5A] text-white rounded-full px-2 py-1 text-[10px] uppercase tracking-[0.15em] inline-flex items-center gap-1 shadow">
          <Lock size={11} weight="bold" /> Locked
        </span>
      </div>
    </div>
  );
}
